/**
 * R218: Auto-sync client persistence (localStorage) → server PlayerState.
 * Subscribes к localStorage CustomEvents и отправляет snapshot на сервер.
 *
 * Триггеры:
 *  - 'aiwari-prof-skills-changed' → sync-prof-skills
 *  - 'aiwari-mat-bag-changed' → sync-mat-bag
 *  - 'aiwari-class-quests-changed' → sync-class-quests
 *  - 'aiwari-discovered-flights-changed' → sync-flights
 *
 * Также отправляет full snapshot при подключении к комнате.
 */

import { getRoom } from './realmClient.js';

interface RoomLike {
  send: (kind: string, msg: unknown) => void;
}

function safeJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function send(kind: string, msg: unknown): void {
  const room = getRoom() as unknown as RoomLike | null;
  if (!room) return;
  try { room.send(kind, msg); } catch { /* not connected */ }
}

function syncProfSkills(): void {
  const skills = safeJSON<Record<string, { id: string; rank: number; cap: number; learned: boolean }>>('aiwari-prof-skills', {});
  const out: Record<string, { rank: number; cap: number }> = {};
  for (const [id, s] of Object.entries(skills)) {
    if (s?.learned) out[id] = { rank: s.rank ?? 1, cap: s.cap ?? 75 };
  }
  send('sync-prof-skills', { skills: out });
}

function syncMatBag(): void {
  const bag = safeJSON<Record<string, number>>('aiwari-mat-bag', {});
  send('sync-mat-bag', { bag });
}

function syncClassQuests(): void {
  const status = safeJSON<Record<string, string>>('aiwari-class-quests', {});
  send('sync-class-quests', { status });
}

function syncFlights(): void {
  const ids = safeJSON<string[]>('aiwari-discovered-flights', []);
  send('sync-flights', { ids });
}

let installed = false;
export function installPersistSync(): void {
  if (installed) return;
  installed = true;

  // Initial snapshot после короткой задержки (после connect).
  const sendAll = () => {
    syncProfSkills();
    syncMatBag();
    syncClassQuests();
    syncFlights();
  };
  setTimeout(sendAll, 1500);

  // Re-sync periodically (раз в 60 секунд) на случай missed events.
  setInterval(sendAll, 60_000);

  // Reactive sync на каждое изменение.
  window.addEventListener('aiwari-prof-skills-changed', syncProfSkills);
  window.addEventListener('aiwari-mat-bag-changed', syncMatBag);
  window.addEventListener('aiwari-class-quests-changed', syncClassQuests);
  window.addEventListener('aiwari-discovered-flights-changed', syncFlights);
}
